// Las piezas con las que una pantalla que entra por enlace (`id_token_hint`,
// `pt`) le dice al usuario sobre qué cuenta está parado. La lógica de cuándo va
// cada una vive en lib/account-guard.ts.
//
//   <AccountLine>            — "Cuenta: juan@gmail.com · No es mi cuenta".
//                              Permanente: nadie debería pagar sin haberla visto.
//   <PayingFor>              — en el paso de pago: "Estás por pagar el Plan X en
//                              la cuenta juan@gmail.com" + pastilla "Cuenta
//                              propia" (verde) / "Cuenta de tercero" (ámbar,
//                              parpadeando). Email COMPLETO: enmascarado, dos
//                              cuentas con las mismas iniciales y dominio se
//                              ven iguales.
//   <AccountConfirmScreen>   — paso intermedio antes de cualquier cambio cuando
//                              la cuenta solo la afirma el enlace.
//   <AccountMismatchScreen>  — bloqueo: el enlace es de otra cuenta. No muestra
//                              ningún dato de esa cuenta (no lo tenemos, a
//                              propósito: el backend no lo manda).

import type { ReactNode } from 'react';
import AuthShell from '@/components/AuthShell';
import { btn } from '@/lib/button-styles';
import type { MismatchReason } from '@/lib/account-guard';

export function AccountLine({
  email,
  onSwitch,
}: {
  email: string | null;
  /** Sin handler no se ofrece el cambio (ej. con un pago abierto en el widget). */
  onSwitch?: (() => void) | null;
}): JSX.Element | null {
  if (!email) return null;
  return (
    <div
      role="group"
      aria-label="Cuenta sobre la que estás operando"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-text"
    >
      <span className="min-w-0 break-all">
        <span className="text-text-muted">Cuenta: </span>
        <strong className="font-semibold">{email}</strong>
      </span>
      {onSwitch ? (
        <button type="button" onClick={onSwitch} className={btn('ghost', 'sm', 'underline -mr-2')}>
          No es mi cuenta
        </button>
      ) : null}
    </div>
  );
}

/**
 * Qué se paga y en qué cuenta, con la pastilla de propiedad.
 *
 * `own` sale SOLO de lo que corroboró el backend (login con contraseña en esta
 * pantalla, o este navegador ya había iniciado sesión como esa cuenta). Que el
 * usuario haya tocado "Sí, es mi cuenta" no la vuelve propia: eso es justo lo
 * que diría quien recibió un enlace ajeno sin leer.
 */
export function PayingFor({
  planName,
  email,
  own,
  onSwitch,
}: {
  planName: string;
  email: string | null;
  own: boolean;
  /** Sin handler no se ofrece el cambio (ej. con un pago abierto en el widget). */
  onSwitch?: (() => void) | null;
}): JSX.Element {
  const plan = /^plan\b/i.test(planName.trim()) ? planName : `Plan ${planName}`;
  return (
    <div
      role="group"
      aria-label="Cuenta en la que vas a pagar"
      data-own={own ? 'true' : 'false'}
      className="paying-for space-y-2 rounded-lg p-3 text-sm text-text"
    >
      {own ? (
        <span className="inline-block rounded-full bg-success px-2.5 py-0.5 text-xs font-semibold text-background">
          Cuenta propia
        </span>
      ) : (
        <span
          role="status"
          className="account-pill-blink inline-block rounded-full bg-warning px-2.5 py-0.5 text-xs font-semibold text-background"
        >
          Cuenta de tercero
        </span>
      )}
      <p>
        Estás por pagar el <strong className="font-semibold">{plan}</strong>{' '}
        {email ? (
          <>
            en la cuenta
            <strong className="block text-base font-semibold [overflow-wrap:anywhere]">{email}</strong>
          </>
        ) : (
          'en esta cuenta'
        )}
      </p>
      {own ? null : (
        <p className="text-xs text-text-muted">
          No pudimos comprobar que esta cuenta sea la tuya: en este navegador no se inició sesión
          con ella. Si no es tu cuenta, no pagues.
        </p>
      )}
      {onSwitch ? (
        <button type="button" onClick={onSwitch} className={btn('ghost', 'sm', 'underline -ml-2')}>
          No es mi cuenta
        </button>
      ) : null}
    </div>
  );
}

export function AccountConfirmScreen({
  email,
  onConfirm,
  onSwitch,
  onBack,
}: {
  email: string | null;
  onConfirm: () => void;
  onSwitch: () => void;
  /** Volver a mirar sin operar. */
  onBack: () => void;
}): JSX.Element {
  return (
    <AuthShell title="¿Es tu cuenta?" subtitle="Confirmalo antes de continuar.">
      <div className="space-y-4">
        <p role="alert" className="rounded-lg border border-border p-4 text-sm text-text">
          {email ? (
            <>
              Vas a operar sobre la cuenta <strong className="break-all font-semibold">{email}</strong>.
              ¿Es tu cuenta?
            </>
          ) : (
            <>
              Entraste con un enlace y no pudimos comprobar que la cuenta sea la tuya. Si no lo
              abriste vos desde la aplicación, iniciá sesión con tu cuenta.
            </>
          )}
        </p>
        <button type="button" onClick={onConfirm} className={btn('primary', 'md', 'w-full')}>
          Sí, es mi cuenta
        </button>
        <button type="button" onClick={onSwitch} className={btn('secondary', 'md', 'w-full')}>
          No, iniciar sesión con otra cuenta
        </button>
        <div className="text-center">
          <button type="button" onClick={onBack} className={btn('ghost', 'sm')}>
            Volver sin hacer cambios
          </button>
        </div>
      </div>
    </AuthShell>
  );
}

const MISMATCH_COPY: Record<MismatchReason, { title: string; body: string }> = {
  other_account: {
    title: 'Este enlace es de otra cuenta',
    body:
      'El enlace con el que entraste corresponde a una cuenta distinta de la que usaste en este ' +
      'dispositivo. Para tu seguridad no podemos continuar con ese enlace.',
  },
  link_used_elsewhere: {
    title: 'Este enlace ya se usó en otro dispositivo',
    body:
      'Cada enlace sirve en un solo navegador. Para tu seguridad no podemos continuar con este: ' +
      'iniciá sesión con tu cuenta para seguir.',
  },
  link_expired: {
    title: 'Este enlace venció',
    body: 'Pasó demasiado tiempo desde que se generó el enlace. Iniciá sesión con tu cuenta para seguir.',
  },
};

export function AccountMismatchScreen({
  onLogin,
  flowExit,
  reason = 'other_account',
}: {
  onLogin: () => void;
  flowExit?: ReactNode;
  reason?: MismatchReason;
}): JSX.Element {
  const copy = MISMATCH_COPY[reason];
  return (
    <AuthShell title={copy.title} flowExit={flowExit}>
      <div className="space-y-4">
        <p role="alert" className="rounded-lg border border-error/30 bg-error/10 p-4 text-sm text-text">
          {copy.body}
        </p>
        <button type="button" onClick={onLogin} className={btn('primary', 'md', 'w-full')}>
          Iniciar sesión con mi cuenta
        </button>
      </div>
    </AuthShell>
  );
}
